import { ReactComponent as TypeIcon } from '../assets/icons/survey-type-icon.svg';
import { ReactComponent as CreatorIcon } from '../assets/icons/creator-icon.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import { ReactComponent as ShareIcon } from '../assets/icons/share.svg';
import { getItemById, getObjectToNumberArray, isObjectEmpty } from './helpers';
import { prepareFilterableUsers } from './uiUtils';
import {
  transformSurveyData,
  transformReviewData,
  transformQuestionnaireSummaryData,
} from './reportUtils';
import { formatDate } from './dateUtils';
import { PARAMS } from '../constants/pages';

export const prepareInitialShareReportData = sharedWith => ({
  share_to: getObjectToNumberArray(sharedWith),
});

export const prepareSurveySummaryTableActionItems = (
  labels,
  report,
  onShare,
  onDelete
) => [
  {
    type: 'share',
    label: labels.share,
    action: onShare(report),
    shouldItemRender: () => true,
    icon: ShareIcon,
  },
  {
    type: 'delete',
    label: labels.delete,
    action: () => onDelete(report),
    shouldItemRender: () => true,
    icon: DeleteIcon,
  },
];

export const prepareSurveySummaryPageFilters = (
  translations,
  surveyTypes,
  allEmployees
) => {
  const { SURVEY_SUMMARY_TYPE, CREATOR } = PARAMS;

  return [
    {
      id: SURVEY_SUMMARY_TYPE,
      name: translations.filters.labels.type,
      icon: TypeIcon,
      isSingleSelect: true,
      items: [{ id: null, name: translations.all }, ...surveyTypes],
    },
    {
      id: CREATOR,
      name: translations.filters.labels[CREATOR],
      icon: CreatorIcon,
      items: prepareFilterableUsers(allEmployees),
    },
  ];
};

export const prepareSurveySummaryHeaderTableActions = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    align: 'right',
    rowCellClass: 'actions-cell',
    isHeaderAction: true,
    minWidth: 60,
    maxWidth: 80,
  },
];

export const prepareSurveySummaryTableHeaders = (labels, canManageReport) => [
  {
    id: 1,
    title: labels.type,
    rowKey: 'type',
    sortAs: 'survey__type',
    minWidth: 77,
    maxWidth: 77,
  },
  {
    id: 2,
    title: labels.title,
    rowKey: 'title',
    sortAs: 'title',
    isHighlighted: true,
    rowCellClass: 'title-cell',
    minWidth: 200,
    maxWidth: '1.5fr',
    hasConditionalTooltip: true,
  },
  {
    id: 3,
    title: labels.createdBy,
    rowKey: 'createdBy',
    rowCellClass: 'created-by-cell',
    sortAs: 'creator',
    maxWidth: '1fr',
  },
  {
    id: 4,
    title: labels.createdAt,
    rowKey: 'createdAt',
    sortAs: 'created_at',
    rowCellClass: 'created-at-cell',
    minWidth: 90,
    maxWidth: '1fr',
  },
  ...(canManageReport
    ? [
        {
          id: 5,
          title: labels.sharedWith,
          rowKey: 'sharedWith',
          rowCellClass: 'shared-with-cell',
          isSortingDisabled: true,
          minWidth: 150,
          maxWidth: '1fr',
        },
      ]
    : []),
];

export const prepareSummaryResults = (
  summaryResults,
  canManageReport,
  surveyTypes,
  renderSurveyType,
  renderUser,
  renderSharedWith,
  renderActions
) => {
  return summaryResults.map(sResult => {
    const { id, title, creator, survey, created_at } = sResult;
    const detailsUrl = `/reports/survey-summary/${id}/details/`;
    const surveyType = getItemById(surveyTypes, survey?.type);

    return {
      id,
      title,
      type: survey ? renderSurveyType(surveyType) : '-',
      createdBy: creator ? renderUser(creator) : '-',
      createdAt: formatDate(created_at),
      rowLink: detailsUrl,
      sharedWith: canManageReport ? renderSharedWith(sResult) : '-',
      ...(canManageReport
        ? {
            actions: renderActions(sResult),
          }
        : {}),
    };
  });
};

export const transformSurveySummaryData = (report, shouldShowOriginalData) => {
  if (isObjectEmpty(report)) return {};

  const survey = transformSurveyData(
    shouldShowOriginalData ? report.raw_data.survey : report.view_data.survey
  );
  const isAnonymizedReport = !!report?.anon_reviewers;
  const sourceData =
    isAnonymizedReport && shouldShowOriginalData
      ? report.raw_data
      : report.view_data;

  return {
    ...report,
    createdAt: report.created_at,
    isAnonymizedReport,
    description: report.description || '',
    survey,
    createdFor: sourceData.subjects,
    reviewers: sourceData.reviewers,
    reviews: (sourceData.attributes || []).map(review =>
      transformReviewData(review, survey.type)
    ),
    questionnaires: (sourceData.questionnaires || []).map(questionnaire =>
      transformQuestionnaireSummaryData(questionnaire, survey.type)
    ),
  };
};
